import { FirebaseAppCheck } from "@capacitor-firebase/app-check";
import { Capacitor } from "@capacitor/core";
import { FirebaseApp } from "firebase/app";
import { CustomProvider, ReCaptchaEnterpriseProvider, ReCaptchaV3Provider, initializeAppCheck } from "firebase/app-check";
import { Util } from "./Utils/Util";

const env = import.meta.env;

// 네이티브 플러그인 initialize 는 앱(바이너리)당 1회만 허용 — 여러 JS 앱 인스턴스(main/super-admin)가
// 같은 네이티브 attestation 을 공유하도록 모듈 레벨에서 memoize.
let nativeReady: Promise<unknown> | null = null;

function ensureNativeAppCheck(): Promise<unknown> {
  if (!nativeReady) {
    nativeReady = FirebaseAppCheck.initialize({
      // 스토어 외 배포(QA FAD) 단말은 Play Integrity/App Attest 를 통과할 수 없으므로
      // dev 빌드는 디버그 provider + 콘솔 등록 토큰을 사용한다 (.env.dev 에서만 true).
      debugToken: env.VITE_APPCHECK_NATIVE_DEBUG === 'true',
      isTokenAutoRefreshEnabled: true,
    }).catch((err) => {
      console.warn('[AppCheck] native initialize 실패 — 미인증으로 계속:', err);
    });
  }
  return nativeReady;
}

/**
 * Firebase App Check 초기화 — 공개 Browser API 키 남용(AI Logic 사건)의 근본 방어책.
 *
 * - 웹: reCAPTCHA v3 또는 Enterprise (VITE_RECAPTCHA_PROVIDER 로 선택 — dev 키는
 *   Enterprise 네이티브 키라 클래식 v3 시크릿 검증이 불가능하다).
 * - 네이티브: Android=Play Integrity, iOS=App Attest(iOS 14 미만 DeviceCheck 폴백)를
 *   @capacitor-firebase/app-check 로 받아 CustomProvider 로 웹 SDK 에 브리지.
 *
 * attestation 실패·플러그인 부재 시 요청은 토큰 없이 나간다(UNENFORCED 와 동일 동작) —
 * 기능 회귀 없음. 차단은 콘솔 enforcement 를 별도 단계로 올릴 때만 발생한다.
 * Firebase 앱 인스턴스별로 각각 호출해야 한다 (main + super-admin).
 */
export function configureAppCheck(app: FirebaseApp) {
  if (Capacitor.isNativePlatform()) {
    // OTA 로 JS 만 갱신된 구버전 바이너리에는 플러그인이 없다 — 조용히 스킵(기존 동작 유지).
    if (!Capacitor.isPluginAvailable('FirebaseAppCheck')) return;
    const ready = ensureNativeAppCheck();
    initializeAppCheck(app, {
      provider: new CustomProvider({
        getToken: async () => {
          await ready;
          const { token, expireTimeMillis } = await FirebaseAppCheck.getToken();
          // expireTimeMillis 는 Android/iOS 에서 항상 오지만 타입상 optional — 보수적 1시간 폴백
          return { token, expireTimeMillis: expireTimeMillis ?? Date.now() + 60 * 60 * 1000 };
        },
      }),
      isTokenAutoRefreshEnabled: true,
    });
    return;
  }

  if (typeof self === 'undefined') return;

  if (Util.isLocalDevelopment()) {
    // 에뮬레이터 로컬 개발은 스킵. 반드시 "로컬" 과 AND — .env.local 의 에뮬레이터 플래그가
    // 배포 번들에 구워진 실사례(dev rc: VITE_USE_EMULATOR_FUNCTIONS="true")에서
    // 배포 사이트의 App Check 초기화까지 통째로 죽는 것을 막는다.
    if (env.VITE_USE_EMULATOR_FUNCTIONS) return;
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = env.VITE_FIREBASE_DEBUG_TOKEN || true;
  }

  const provider = env.VITE_RECAPTCHA_PROVIDER === 'enterprise'
    ? new ReCaptchaEnterpriseProvider(env.VITE_RECAPTCHA_SITE_KEY)
    : new ReCaptchaV3Provider(env.VITE_RECAPTCHA_SITE_KEY);
  initializeAppCheck(app, { provider, isTokenAutoRefreshEnabled: true });
}
